import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";

export function ContactSection({ noBorder = false }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We will get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-base placeholder:text-fog transition-colors duration-300";

  return (
    <section id="contact" className={`bg-white ${noBorder ? "" : "border-t border-line"}`} data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24 grid md:grid-cols-2 gap-12 md:gap-20">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Start a<br />
            <span className="italic text-ember">conversation</span>.
          </h2>
          <p className="mt-8 text-fog leading-relaxed max-w-md">
            Questions, collaborations, or comments: send us a message and we will get back to you soon.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
            <input
              data-testid="contact-name-input"
              className={inputCls}
              placeholder="Your name"
              aria-label="Your name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              data-testid="contact-email-input"
              type="email"
              className={inputCls}
              placeholder="Your email"
              aria-label="Your email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <textarea
              data-testid="contact-message-input"
              className={`${inputCls} min-h-[120px] resize-none`}
              placeholder="Your message"
              aria-label="Your message"
              required
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group inline-flex items-center gap-3 border border-ink px-8 py-4 text-sm tracking-wide hover:bg-ink hover:text-bone transition-colors duration-300 disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send message"}
              <span className="w-1.5 h-1.5 rounded-full bg-ember transition-transform duration-300 group-hover:scale-150" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
